import { Product } from '../types';
import { ProductVariant } from '../hooks/useSupabaseProducts';

export interface PriceInfo {
  finalPrice: number;
  originalPrice: number;
  discount: number;
  quantity: number;
  perPill: number;
}

/**
 * Pick the variant that should drive the "starting at" price on product cards.
 * Rule: `is_default` wins, otherwise the lowest strength / display_order.
 */
export function getDefaultVariant(product: Product): ProductVariant | null {
  const variants = product.variants || [];
  if (variants.length === 0) return null;
  const def = variants.find(v => v.is_default);
  return def || variants[0];
}

/**
 * "Starting at" price info for a product family, derived from its default variant's
 * cheapest quantity tier. Returns zeroed values if no variant/price data is available.
 */
export function getProductPriceInfo(product: Product): PriceInfo {
  const variant = getDefaultVariant(product);
  const prices = variant?.prices || [];
  if (prices.length === 0) {
    return { finalPrice: 0, originalPrice: 0, discount: 0, quantity: 0, perPill: 0 };
  }

  const sortedPrices = [...prices].sort((a, b) => a.quantity - b.quantity);
  const lowestPrice = sortedPrices[0];
  const maxDiscount = Math.max(
    0,
    ...sortedPrices.map(p =>
      p.original_price > 0 ? Math.round((1 - p.sale_price / p.original_price) * 100) : 0
    )
  );

  const quantity = lowestPrice.quantity || 1;
  return {
    finalPrice: lowestPrice.sale_price,
    originalPrice: lowestPrice.original_price,
    discount: maxDiscount,
    quantity,
    perPill: quantity > 0 ? +(lowestPrice.sale_price / quantity).toFixed(2) : 0,
  };
}
